import { useState, useMemo } from 'react';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { TRIAGEM_TH, TRIAGEM_TD, TRIAGEM_SELO_BASE, TRIAGEM_SELO_PENDENTE } from '@/components/kanban/triagem/triagemTabela';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2, Check, Clock, AlertTriangle, Download, Plus, History, MessageSquare, ShieldAlert, Ban } from 'lucide-react';
import { CancelarItemModal } from '@/components/pedido/CancelarItemModal';
import { usePortabilidadeExtras, usePedirTitularidade, horasDesde } from '@/hooks/usePortabilidade';
import { HistoricoPortabilidadeDialog, TratativaDialog } from './PortabilidadeDialogs';
import { AcoesTriagem, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

import { useTriagemCpfDivergente, type ItemTriagemCpfDivergente } from './useTriagemCpfDivergente';
import { TriagemStandardHeaders, TriagemStandardCells, TriagemStandardColGroup, type TriagemStandardItem } from './TriagemStandardColumns';
import { TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO, TRIAGEM_LARGURA_MINIMA_PADRAO } from '@/components/kanban/triagem/triagemTabela';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function TriagemCpfDivergenteModal({ isOpen, onClose }: Props) {
  const [novoChamadoItem, setNovoChamadoItem] = useState<string | null>(null);
  const [novoChamadoNumero, setNovoChamadoNumero] = useState('');
  const [resolverItem, setResolverItem] = useState<string | null>(null);
  const [novaCotacao, setNovaCotacao] = useState('');
  const [tratativa, setTratativa] = useState<{ item: ItemTriagemCpfDivergente; manter: boolean } | null>(null);
  const [historico, setHistorico] = useState<{ itemId: string; razaoSocial: string; numeroPedido: string } | null>(null);
  const [cancelarItem, setCancelarItem] = useState<ItemTriagemCpfDivergente | null>(null);
  const pedirTitularidade = usePedirTitularidade();

  const {
    itens: itensTodos, isLoading, refetch,
    registrarChamado,
    resolverComNovaCotacao,
    moverParaFinal,
    downloadDocCedente,
  } = useTriagemCpfDivergente();
  /* Só a linha clicada fica ocupada; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const em = (i: ItemTriagemCpfDivergente, acao: string) => ocupada(`${i.item_id}|${acao}`);
  const linhaOcupada = (i: ItemTriagemCpfDivergente) => ['manter', 'resolver', 'chamado', 'titular'].some((a) => em(i, a));
  const rodar = (i: ItemTriagemCpfDivergente, acao: string, fn: () => Promise<unknown>) => {
    void ocupar(`${i.item_id}|${acao}`, fn).catch(() => undefined);
  };

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const itens = useMemo(
    () => itensTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)),
    [itensTodos, segmento],
  );

  const { data: extras } = usePortabilidadeExtras(itens.map(i => i.item_id), isOpen);
  // Prazo da titularidade: 48h na fila (Douglas 24/09/2026). Sem tratativa ou
  // passado o prazo, a linha fica bordô.
  const PRAZO_HORAS = 48;
  const foraDoPrazo = itens.filter(i => (horasDesde(extras?.get(i.item_id)?.entrou_coluna_em ?? i.entrada_coluna_em) ?? 0) > PRAZO_HORAS).length;
  const semTratativa = itens.filter(i => !extras?.get(i.item_id)?.ultima_tratativa).length;
  const uniquePedidos = new Set(itens.map(i => i.pedido_id)).size;

  const handleRegistrarChamado = (item: ItemTriagemCpfDivergente) => {
    const numeroChamado = novoChamadoNumero.trim();
    rodar(item, 'chamado', () => registrarChamado({
      itemId: item.item_id,
      pedidoId: item.pedido_id,
      numeroChamado,
      cnpj: item.cnpj,
    }));
    setNovoChamadoNumero('');
    setNovoChamadoItem(null);
  };

  const handleResolver = (item: ItemTriagemCpfDivergente) => {
    if (!novaCotacao.trim()) return;
    const cotacao = novaCotacao.trim();
    rodar(item, 'resolver', () => resolverComNovaCotacao({
      itemId: item.item_id,
      novaCotacao: cotacao,
      cnpj: item.cnpj,
    }));
    setNovaCotacao('');
    setResolverItem(null);
  };

  const toStdItem = (item: ItemTriagemCpfDivergente): TriagemStandardItem => ({
    item_id: item.item_id,
    coluna_atual: 'CPF / CNPJ DIVERGENTE',
    pedido_id: item.pedido_id,
    numero_pedido: item.numero_pedido,
    item_seq: item.item_seq ?? null,
    cnpj: item.cnpj,
    tipo_pessoa: item.tipo_pessoa,
    documento: item.documento || item.cnpj,
    razao_social: item.razao_social,
    numero_cotacao_operadora: item.numero_cotacao_operadora || '',
    linhas_formatadas: item.linhas_formatadas || [],
    operadora_doadora: item.operadora_doadora || '-',
    plano_nome: item.plano_nome || '',
    tipo_chip: item.tipo_chip || '',
    faturamento_tipo: item.faturamento_tipo || '',
    entrada_coluna_em: item.entrada_coluna_em,
    agenda_tipo: item.agenda_tipo || '',
    data_agendamento: item.data_agendamento || '',
    cessionario_nome: item.cessionario_nome || '',
    cessionario_cpf: item.cessionario_cpf || '',
    is_first_of_cnpj: item.is_first_of_cnpj,
    cliente_id: item.cliente_id || '',
  });

  return (
    <>
      <TriagemShell
        isOpen={isOpen}
        onFechar={onClose}
        Icone={AlertTriangle}
        titulo="Triagem Titularidade · CPF/CNPJ Divergente"
        contadores={{ pedidos: uniquePedidos, itens: itens.length, valor: 0 }}
        cabecalhoExtra={<span className={cn('whitespace-nowrap rounded-lg border border-border bg-card px-3 py-1 text-[11px]', (semTratativa || foraDoPrazo) ? 'text-primary' : 'text-foreground')}>
          {semTratativa} sem tratativa · {foraDoPrazo} passaram de 48h</span>}
        segmento={segmento}
        onSegmento={setSegmento}
        sla={null}
        fifo={false}
        legenda={[
          { key: 'manter', Icone: Clock, label: 'Tratativa e manter' },
          { key: 'resolver', Icone: Check, label: 'Resolver' },
          { key: 'tratativa', Icone: ShieldAlert, label: 'Tratativa' },
          { key: 'chamado', Icone: Plus, label: 'Chamado IW' },
          { key: 'whatsapp', Icone: MessageSquare, label: 'Pedir titular ao cliente' },
          { key: 'doc', Icone: Download, label: 'Doc. Cedente' },
          { key: 'historico', Icone: History, label: 'Histórico' },
          { key: 'cancelar', Icone: Ban, label: 'Cancelar (titularidade)' },
        ]}
        isLoading={isLoading}
        onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
        vazio={
          itens.length === 0
            ? { titulo: 'Nenhum item pendente', descricao: 'Todos os itens foram processados' }
            : undefined
        }
      >
              <table className="w-full" style={{ tableLayout: 'fixed', minWidth: TRIAGEM_LARGURA_MINIMA_PADRAO }}>
                <TriagemStandardColGroup extraCols={<><col style={{ width: '14%' }} /><col style={{ width: '5%' }} /><col style={{ width: '4%' }} /><col style={{ width: '330px' }} /></>} />
                <thead className="sticky top-0 z-10 bg-surface">
                  <tr>
                    <TriagemStandardHeaders rotuloDocumento={rotuloSegmento(segmento)} />
                    <th className={TRIAGEM_TH}>Tratativa</th>
                    <th className={TRIAGEM_TH} title="Prazo da fila de titularidade: 48 horas">Prazo</th>
                    <th className={TRIAGEM_TH}>Chamados</th>
                    <th className={TRIAGEM_TH}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((item) => {
                    const ex = extras?.get(item.item_id);
                    const horas = horasDesde(ex?.entrou_coluna_em ?? item.entrada_coluna_em) ?? 0;
                    const atrasada = horas > PRAZO_HORAS;
                    const ult = ex?.ultima_tratativa;
                    const alvoHist = { itemId: item.item_id, razaoSocial: item.razao_social, numeroPedido: item.numero_pedido };
                    const isAnyMoving = linhaOcupada(item);
                    return (
                    <tr key={item.item_id} className={cn('box-border h-[33px] border-b border-border/30 transition-colors hover:bg-muted/30',
                      !item.is_first_of_cnpj && 'bg-muted/10', (atrasada || !ult) && 'shadow-[inset_3px_0_0_hsl(var(--primary))]')}>
                      <TriagemStandardCells item={toStdItem(item)} />
                      <td className={TRIAGEM_TD}>
                        <button type="button" onClick={() => ult ? setHistorico(alvoHist) : setTratativa({ item, manter: false })}
                          className={cn('block w-full truncate text-left text-[10px]', ult ? 'text-foreground' : 'text-primary')}
                          title={ult ? `${[ult.motivo, ult.acao].filter(Boolean).join(' · ')}\n${ult.texto}${ult.por ? `\n— ${ult.por}` : ''}` : 'Sem tratativa — registrar por que está errado e o que está sendo feito'}>
                          {ult ? [ult.motivo, ult.acao].filter(Boolean).join(' · ') || ult.texto : 'Sem tratativa — registrar'}
                        </button>
                      </td>
                      <td className={TRIAGEM_TD}>
                        <span className={cn(TRIAGEM_SELO_BASE, atrasada ? TRIAGEM_SELO_PENDENTE : 'text-muted-foreground')}
                          title={atrasada ? 'Passou das 48h desta fila' : `Restam ${Math.max(0, Math.round(PRAZO_HORAS - horas))}h do prazo de 48h`}>
                          {atrasada ? `+${Math.round(horas - PRAZO_HORAS)}h` : `${Math.max(0, Math.round(PRAZO_HORAS - horas))}h`}
                        </span>
                      </td>
                      {/* Chamados */}
                      <td className="py-1.5 px-2">
                        {item.chamados.length > 0 ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 px-2 gap-1">
                                <History className="h-3 w-3" />
                                <Badge variant="secondary" className="text-[10px] px-1 py-0">{item.chamados.length}</Badge>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-3" align="start">
                              <p className="text-xs font-semibold mb-2">Histórico de Chamados IW</p>
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {item.chamados.map((c, i) => (
                                  <div key={c.id} className="flex items-start gap-2 text-xs border-b border-border/30 pb-1.5">
                                    <Badge variant="outline" className="text-[10px] px-1 shrink-0">#{i + 1}</Badge>
                                    <div className="flex-1 min-w-0">
                                      <p className="tabular-nums font-medium truncate">{c.numero_chamado}</p>
                                      <p className="text-muted-foreground">{formatDate(c.created_at)}</p>
                                    </div>
                                    <Badge variant={c.status === 'RESOLVIDO' ? 'default' : 'secondary'} className="text-[10px] px-1 shrink-0">
                                      {c.status}
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      {/* Ações */}
                      <td className="py-1.5 px-2">
                        <AcoesTriagem
                          manter={
                            <BotaoManter
                              title="Manter — consultei e ainda não mudou nada. Registrar a tratativa e mover para o fim da fila."
                              onClick={() => setTratativa({ item, manter: true })}
                              disabled={isAnyMoving}
                              carregando={em(item, 'manter')}
                            />
                          }
                          avancar={
                              <Popover open={resolverItem === item.item_id} onOpenChange={(open) => {
                                setResolverItem(open ? item.item_id : null);
                                if (!open) setNovaCotacao('');
                              }}>
                                <PopoverTrigger asChild>
                                  <Button
                                    size="icon" variant="ghost"
                                    className={TRIAGEM_BTN_PRIMARIO}
                                    title="Resolver - Nova Cotação IW → Inspeção Concluída"
                                    disabled={isAnyMoving}
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-3" align="end">
                                  <p className="text-xs font-semibold mb-2">Resolver CPF Divergente</p>
                                  <p className="text-[10px] text-muted-foreground mb-2">Informe a nova cotação IW. O item será movido para Inspeção Concluída.</p>
                                  <div className="flex gap-2">
                                    <Input
                                      placeholder="Nova Cotação IW"
                                      value={novaCotacao}
                                      onChange={(e) => setNovaCotacao(e.target.value)}
                                      className="h-8 text-xs"
                                      onKeyDown={(e) => e.key === 'Enter' && handleResolver(item)}
                                    />
                                    <Button
                                      size="sm"
                                      className="h-8 px-3"
                                      onClick={() => handleResolver(item)}
                                      disabled={!novaCotacao.trim() || em(item, 'resolver')}
                                    >
                                      {em(item, 'resolver') ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Resolver'}
                                    </Button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                          }
                          desvios={
                            <>
                              <Button
                                size="icon" variant="ghost"
                                className={TRIAGEM_BTN}
                                title="Registrar tratativa: por que está errado e o que está sendo feito"
                                onClick={() => setTratativa({ item, manter: false })}
                                disabled={isAnyMoving}
                              >
                                <ShieldAlert className="h-3.5 w-3.5" />
                              </Button>
                              <Popover open={novoChamadoItem === item.item_id} onOpenChange={(open) => {
                                setNovoChamadoItem(open ? item.item_id : null);
                                if (!open) setNovoChamadoNumero('');
                              }}>
                                <PopoverTrigger asChild>
                                  <Button
                                    size="icon" variant="ghost"
                                    className={TRIAGEM_BTN}
                                    title="Registrar chamado IW"
                                    disabled={isAnyMoving}
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 p-3" align="end">
                                  <p className="text-xs font-semibold mb-2">Novo Chamado IW</p>
                                  <div className="flex gap-2">
                                    <Input
                                      placeholder="Nº do chamado"
                                      value={novoChamadoNumero}
                                      onChange={(e) => setNovoChamadoNumero(e.target.value)}
                                      className="h-8 text-xs"
                                      onKeyDown={(e) => e.key === 'Enter' && handleRegistrarChamado(item)}
                                    />
                                    <Button
                                      size="sm"
                                      className="h-8 px-3"
                                      onClick={() => handleRegistrarChamado(item)}
                                      disabled={!novoChamadoNumero.trim() || em(item, 'chamado')}
                                    >
                                      {em(item, 'chamado') ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Salvar'}
                                    </Button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </>
                          }
                          ferramentas={
                            <>
                              <Button
                                size="icon" variant="ghost"
                                className={TRIAGEM_BTN}
                                title="Pedir ao cliente, pela conversa do pedido, em nome de quem cada linha está hoje"
                                onClick={() => rodar(item, 'titular', () => pedirTitularidade.mutateAsync(item.item_id))}
                                disabled={isAnyMoving}
                              >
                                {em(item, 'titular') ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />}
                              </Button>
                              <Button
                                size="icon" variant="ghost"
                                className={TRIAGEM_BTN}
                                title={item.has_doc_cedente ? 'Download Doc. Cedente' : 'Doc. Cedente não encontrado'}
                                onClick={() => item.doc_cedente_path && downloadDocCedente(item.doc_cedente_path)}
                                disabled={!item.has_doc_cedente || isAnyMoving}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className={TRIAGEM_BTN}
                                title="Histórico da portabilidade" onClick={() => setHistorico(alvoHist)}>
                                <History className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          }
                          cancelar={
                              <Button
                                size="icon" variant="ghost"
                                className={TRIAGEM_BTN}
                                title={ult ? 'Cancelar por titularidade — sem solução' : 'Registre a tratativa antes de cancelar por titularidade'}
                                onClick={() => setCancelarItem(item)}
                                disabled={isAnyMoving || !ult}
                              >
                                <Ban className="h-3.5 w-3.5" />
                              </Button>
                          }
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
      </TriagemShell>

      <TratativaDialog
        alvo={tratativa ? { itemId: tratativa.item.item_id, pedidoId: tratativa.item.pedido_id, razaoSocial: tratativa.item.razao_social, numeroPedido: tratativa.item.numero_pedido } : null}
        onClose={() => setTratativa(null)}
        rotuloSalvar={tratativa?.manter ? 'Salvar e mover para o fim' : 'Salvar tratativa'}
        onSalvo={() => {
          if (tratativa?.manter) {
            const alvo = tratativa.item;
            rodar(alvo, 'manter', () => moverParaFinal({ itemId: alvo.item_id, pedidoId: alvo.pedido_id, cnpj: alvo.cnpj }));
          }
        }}
      />
      <HistoricoPortabilidadeDialog alvo={historico} onClose={() => setHistorico(null)} />
      {cancelarItem && (
        <CancelarItemModal
          open={!!cancelarItem}
          onOpenChange={(o) => !o && setCancelarItem(null)}
          itemId={cancelarItem.item_id}
          pedidoId={cancelarItem.pedido_id}
          itemNome={`${cancelarItem.razao_social} - ${cancelarItem.numero_pedido}`}
          onSuccess={() => { setCancelarItem(null); refetch(); }}
        />
      )}
    </>
  );
}
